import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiFout, api } from '@/lib/api';
import type { SessieAntwoord } from '@/lib/api-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import { ArrowLeft, Download, Loader2, Save, ShieldX } from 'lucide-react';
import { UserCostsTab } from '@/components/settings/UserCostsTab';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { PROVIDER_ROLES, POH_SPECIALIZATIONS, REPORT_VERBOSITY_OPTIONS } from '@/types/practice-settings';
import { VOICE_TRANSLATE_COMMAND_ENABLED } from '@/lib/feature-flags';
import { InfoBanner } from '@/components/InfoBanner';

export default function UserSettingsPage() {
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [practiceUserId, setPracticeUserId] = useState<string | null>(null);
  const [practiceName, setPracticeName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [externalUserId, setExternalUserId] = useState('');

  // User-level settings (null = use practice default)
  const [autoTranslate, setAutoTranslate] = useState<boolean | null>(null);
  const [autoSuggestIcpc, setAutoSuggestIcpc] = useState<boolean | null>(null);
  const [doctorPhrase, setDoctorPhrase] = useState<string | null>(null);
  const [readTriggerPhrase, setReadTriggerPhrase] = useState<string | null>(null);
  const [providerRole, setProviderRole] = useState<string | null>(null);
  const [providerSpecializations, setProviderSpecializations] = useState<string[] | null>(null);
  const [reportVerbosity, setReportVerbosity] = useState<string | null>(null);
  const [personalProfile, setPersonalProfile] = useState<string | null>(null);
  const [forceSingleSoep, setForceSingleSoep] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState(false);

  // SIP overrides (null = praktijkstandaard)
  const [sipHost, setSipHost] = useState<string | null>(null);
  const [sipWsPort, setSipWsPort] = useState<string | null>(null);
  const [sipWsPath, setSipWsPath] = useState<string | null>(null);
  const [sipExtension, setSipExtension] = useState<string | null>(null);
  const [sipPassword, setSipPassword] = useState<string | null>(null);
  const [sipDisplayName, setSipDisplayName] = useState<string | null>(null);
  const [sipStunServer, setSipStunServer] = useState<string | null>(null);
  const [sipDialPrefix, setSipDialPrefix] = useState<string | null>(null);

  // Practice defaults for display
  const [practiceDefaults, setPracticeDefaults] = useState<Record<string, unknown>>({});

  /**
   * De gegevens komen uit de sessie, niet uit een tweede tokenruil.
   *
   * Deze pagina riep validate-access-token opnieuw aan met de tokens uit de URL. Dat
   * had een neveneffect dat er niet hoort te zijn: die functie maakte bij een onbekend
   * token een nieuwe praktijk aan. Het openen van een instellingenscherm kon dus een
   * praktijk laten ontstaan.
   *
   * GET /api/me is ophalen en niets anders, en de tokens zijn niet meer nodig -- de
   * sessie zit in een HttpOnly-cookie. Daarmee verdwijnen ze ook uit de URL van deze
   * pagina en dus uit de browsergeschiedenis.
   */
  useEffect(() => {
    void (async () => {
      try {
        const antwoord = await api.get<SessieAntwoord>('/me');

        if (!antwoord.user) {
          // Een praktijksessie zonder gebruiker: het HIS stuurde geen user_id mee. Er
          // zijn dan geen persoonlijke instellingen om te tonen.
          setIsAuthorized(false);
          return;
        }

        const u = antwoord.user;
        const p = antwoord.practice;

        setIsAuthorized(true);
        setPracticeName(p.practiceName);
        setPracticeUserId(u.id);
        setDisplayName(u.displayName || '');
        setExternalUserId(u.externalUserId);

        // null betekent hier "gebruik de praktijkstandaard"; dat onderscheid moet
        // blijven, dus geen ?? '' erop.
        setAutoTranslate(u.autoTranslate);
        setAutoSuggestIcpc(u.autoSuggestIcpc);
        setDoctorPhrase(u.doctorPhrase);
        setReadTriggerPhrase(u.readTriggerPhrase);
        setProviderRole(u.providerRole);
        setProviderSpecializations(u.providerSpecializations);
        setReportVerbosity(u.reportVerbosity);
        setPersonalProfile(u.personalProfile);
        setForceSingleSoep(u.forceSingleSoep);
        setVerifiedEmail(u.email);
        setEmailVerified(u.emailVerified);

        /**
         * De SIP-velden komen uit `sip`, en dat is de samengevoegde waarde --
         * gebruiker boven praktijk. Voor dit scherm is dat niet ideaal: het wil weten
         * wat de gebruiker zelf heeft gezet. De praktijkstandaard staat in
         * practiceSip, dus een veld dat daarmee overeenkomt is niet overschreven.
         */
        const sip = antwoord.sip;
        const pSip = antwoord.practiceSip ?? null;
        const eigen = (waarde: string | null | undefined, standaard: string | null | undefined) =>
          waarde && waarde !== standaard ? waarde : null;

        setSipHost(eigen(sip?.host, pSip?.host));
        setSipWsPort(eigen(sip?.wsPort === '8089' ? null : sip?.wsPort, pSip?.wsPort));
        setSipWsPath(eigen(sip?.wsPath, pSip?.wsPath));
        setSipExtension(eigen(sip?.extension, pSip?.extension));
        setSipPassword(sip?.password ?? null);
        setSipDisplayName(eigen(sip?.displayName, pSip?.displayName));
        setSipStunServer(eigen(sip?.stunServer, pSip?.stunServer));
        setSipDialPrefix(eigen(sip?.dialPrefix, pSip?.dialPrefix));

        setPracticeDefaults({
          auto_translate: p.autoTranslate,
          auto_suggest_icpc: p.autoSuggestIcpc,
          doctor_phrase: p.doctorPhrase,
          read_trigger_phrase: p.readTriggerPhrase,
          sip_host: pSip?.host ?? '',
          sip_ws_port: pSip?.wsPort === '8089' ? '' : (pSip?.wsPort ?? ''),
          sip_ws_path: pSip?.wsPath ?? '',
          sip_extension: pSip?.extension ?? '',
          sip_display_name: pSip?.displayName ?? '',
          sip_stun_server: pSip?.stunServer ?? '',
          sip_dial_prefix: pSip?.dialPrefix ?? '',
        });
      } catch {
        setIsAuthorized(false);
      } finally {
        setIsValidating(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    if (!practiceUserId) return;
    setIsSaving(true);
    try {
      // Een expliciete allowlist op de server bepaalt wat een gebruiker van zichzelf
      // mag wijzigen. In de bron ging dit als een vrij `updates`-object naar
      // update-practice-user; die functie had als enige wél een allowlist, maar dat
      // was niet aan de aanroep te zien.
      await api.patch('/me', {
        displayName,
        autoTranslate,
        autoSuggestIcpc,
        doctorPhrase,
        readTriggerPhrase,
        providerRole,
        providerSpecializations,
        reportVerbosity,
        personalProfile,
        forceSingleSoep,
        sipHost,
        sipWsPort: sipWsPort === '8089' ? null : sipWsPort,
        sipWsPath,
        sipExtension,
        sipPassword,
        sipDisplayName,
        sipStunServer,
        sipDialPrefix,
      });
      toast({ title: 'Opgeslagen', description: 'Instellingen zijn bijgewerkt.' });
    } catch {
      toast({ title: 'Fout', description: 'Kon instellingen niet opslaan.', variant: 'destructive' });
    }
    setIsSaving(false);
  };

  const handleBack = () => {
    // Zonder query-parameters: de sessie zit in een cookie, dus het opnamescherm hoeft
    // de tokens niet meer terug te krijgen. Ze stonden hier in de URL en dus in de
    // browsergeschiedenis.
    navigate('/');
  };

  /**
   * Het addendum met de praktijknaam erin.
   *
   * De browser stempelde die naam eerst zelf met pdf-lib, met de coordinaten van
   * de plaatshouderregel hier hardgecodeerd. Dat ging twee keer mis: de
   * coordinaten raakten achterhaald en dekten andere tekst af, en de bijlage bij
   * de verificatiemail werd langs deze weg helemaal niet ingevuld -- juist het
   * exemplaar dat de praktijk als eerste ziet. De API doet het nu voor beide
   * wegen, met de coordinaten op een plek: lib/vwo-pdf.ts.
   */
  const handleDownloadDpa = useCallback(async () => {
    try {
      const { blob, headers } = await api.blobMetHeaders('/me/verwerkersovereenkomst');

      // De route zet de bestandsnaam in content-disposition, met de praktijknaam
      // zoals de API die kent.
      const uitHeader = /filename="([^"]+)"/.exec(headers.get('content-disposition') ?? '');

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = uitHeader?.[1] ?? 'Verwerkersovereenkomst-AI-diensten.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (fout) {
      toast({
        title: 'Fout',
        description: fout instanceof ApiFout ? fout.message : 'Kon PDF niet downloaden.',
        variant: 'destructive',
      });
    }
  }, []);

  if (isValidating) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <ShieldX className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="text-lg font-semibold">Geen toegang</h2>
          <p className="text-sm text-muted-foreground">Ongeldige token of gebruiker.</p>
        </div>
      </div>
    );
  }

  const effectiveBool = (userVal: boolean | null, practiceKey: string) =>
    userVal ?? (practiceDefaults[practiceKey] as boolean) ?? true;

  const effectiveStr = (userVal: string | null, practiceKey: string) =>
    userVal ?? (practiceDefaults[practiceKey] as string) ?? '';

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* Boven de eigen kop: de melding hoort de bovenste regel van het scherm te zijn. */}
      <InfoBanner />
      <div className="px-3 py-2 bg-primary/10 border-b flex items-center gap-2 shrink-0">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium">Instellingen</span>
        <span className="text-xs text-muted-foreground ml-auto">{practiceName}</span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 max-w-md mx-auto w-full">
        <Tabs defaultValue="settings">
          <TabsList className="w-full">
            <TabsTrigger value="settings" className="flex-1">Instellingen</TabsTrigger>
            <TabsTrigger value="sip" className="flex-1">SIP</TabsTrigger>
            <TabsTrigger value="costs" className="flex-1">Kosten</TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="mt-4 space-y-6">
            {/* Profile */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Gebruikersprofiel</h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Gebruiker ID</Label>
                  <Input value={externalUserId} disabled className="mt-1 text-xs bg-muted" />
                </div>
                <div>
                  <Label className="text-xs">Weergavenaam</Label>
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Bijv. Dr. Jansen"
                    className="mt-1 text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs">Geverifieerd e-mailadres</Label>
                  {verifiedEmail ? (
                    <div className="mt-1 flex items-center gap-2">
                      <Input value={verifiedEmail} disabled className="text-xs bg-muted flex-1" />
                      <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${emailVerified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {emailVerified ? '✓ Geverifieerd' : 'Niet geverifieerd'}
                      </span>
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-muted-foreground italic">Nog geen e-mailadres geverifieerd</p>
                  )}
                </div>
              </div>
            </div>

            {/* User-level settings */}
            <div>
              <h3 className="text-sm font-semibold mb-1">Persoonlijke instellingen</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Overschrijf de praktijkinstellingen voor jouw account. Leeg = praktijkstandaard.
              </p>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-xs">Automatisch vertalen</Label>
                    <p className="text-[10px] text-muted-foreground">
                      Praktijk: {practiceDefaults.auto_translate ? 'aan' : 'uit'}
                    </p>
                  </div>
                  <Switch
                    checked={effectiveBool(autoTranslate, 'auto_translate')}
                    onCheckedChange={(v) => setAutoTranslate(v)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-xs">ICPC suggesties</Label>
                    <p className="text-[10px] text-muted-foreground">
                      Praktijk: {practiceDefaults.auto_suggest_icpc ? 'aan' : 'uit'}
                    </p>
                  </div>
                  <Switch
                    checked={effectiveBool(autoSuggestIcpc, 'auto_suggest_icpc')}
                    onCheckedChange={(v) => setAutoSuggestIcpc(v)}
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Zorgverleners herkenningszinnen</Label>
                  <Input
                    value={effectiveStr(doctorPhrase, 'doctor_phrase')}
                    onChange={(e) => setDoctorPhrase(e.target.value)}
                    placeholder="Bijv. 'Hier spreekt de arts, Goedemorgen dokter'"
                    className="text-xs"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Scheid meerdere zinnen met een komma. Praktijk: {(practiceDefaults.doctor_phrase as string) || '(leeg)'}
                  </p>
                </div>

                {VOICE_TRANSLATE_COMMAND_ENABLED && (
                  <div className="space-y-1">
                    <Label className="text-xs">Vertaal trigger</Label>
                    <Input
                      value={effectiveStr(readTriggerPhrase, 'read_trigger_phrase')}
                      onChange={(e) => setReadTriggerPhrase(e.target.value)}
                      placeholder="Bijv. 'Lees voor'"
                      className="text-xs"
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Praktijk: {(practiceDefaults.read_trigger_phrase as string) || '(leeg)'}
                    </p>
                  </div>
                )}

                {/* Provider Role */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label className="text-xs font-semibold">Zorgverlener rol</Label>
                  <p className="text-[10px] text-muted-foreground">Selecteer uw functie voor aangepaste verslaglegging.</p>
                  <RadioGroup
                    value={providerRole || ''}
                    onValueChange={(v) => setProviderRole(v || null)}
                    className="gap-1.5"
                  >
                    {PROVIDER_ROLES.map((role) => (
                      <div key={role.value} className="flex items-center space-x-2">
                        <RadioGroupItem value={role.value} id={`role-${role.value}`} />
                        <Label htmlFor={`role-${role.value}`} className="text-xs font-normal cursor-pointer">
                          {role.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* PoH Specializations */}
                {providerRole === 'poh' && (
                  <div className="space-y-2 pl-4">
                    <Label className="text-xs">Specialisatie(s)</Label>
                    <p className="text-[10px] text-muted-foreground">Meerdere opties mogelijk.</p>
                    <div className="space-y-1.5">
                      {POH_SPECIALIZATIONS.map((spec) => {
                        const checked = (providerSpecializations || []).includes(spec.value);
                        return (
                          <div key={spec.value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`spec-${spec.value}`}
                              checked={checked}
                              onCheckedChange={(v) => {
                                const current = providerSpecializations || [];
                                if (v) {
                                  setProviderSpecializations([...current, spec.value]);
                                } else {
                                  setProviderSpecializations(current.filter(s => s !== spec.value));
                                }
                              }}
                            />
                            <Label htmlFor={`spec-${spec.value}`} className="text-xs font-normal cursor-pointer">
                              {spec.label}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Altijd 1 SOEP (assistente / PoH) */}
                {(providerRole === 'poh' || providerRole === 'assistent') && (
                  <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
                    <div className="space-y-0.5">
                      <Label className="text-xs font-semibold">Altijd één SOEP-verslag</Label>
                      <p className="text-[10px] text-muted-foreground">
                        Ook als er meerdere somatische onderwerpen besproken worden, wordt alles in één SOEP samengevat.
                      </p>
                    </div>
                    <Switch checked={forceSingleSoep} onCheckedChange={setForceSingleSoep} />
                  </div>
                )}

                {/* Report Verbosity */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label className="text-xs font-semibold">Verslaglegging stijl</Label>
                  <p className="text-[10px] text-muted-foreground">Hoe uitgebreid moet de SOEP-samenvatting zijn?</p>
                  <RadioGroup
                    value={reportVerbosity || 'gemiddeld'}
                    onValueChange={(v) => setReportVerbosity(v)}
                    className="gap-1.5"
                  >
                    {REPORT_VERBOSITY_OPTIONS.map((opt) => (
                      <div key={opt.value} className="flex items-center space-x-2">
                        <RadioGroupItem value={opt.value} id={`verbosity-${opt.value}`} />
                        <Label htmlFor={`verbosity-${opt.value}`} className="text-xs font-normal cursor-pointer">
                          {opt.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* Personal Profile */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label className="text-xs font-semibold">Persoonlijk profiel</Label>
                  <p className="text-[10px] text-muted-foreground">
                    Vrije tekst die wordt meegegeven aan de AI voor gepersonaliseerde verslaglegging.
                  </p>
                  <textarea
                    value={personalProfile || ''}
                    onChange={(e) => setPersonalProfile(e.target.value || null)}
                    placeholder="Bijv. Ik werk in een achterstandswijk met veel niet-westerse migranten. Ik noteer altijd de BMI en bloeddruk bij elk consult. Gebruik bij voorkeur de term 'beweegadvies' in plaats van 'sport'."
                    className="w-full text-xs bg-background border border-input rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-ring min-h-[5rem] placeholder:text-muted-foreground/60"
                    rows={4}
                  />
                </div>
              </div>
            </div>

            {/* Verwerkersovereenkomst download */}
            <div className="pt-2 border-t border-border">
              <h3 className="text-sm font-semibold mb-2">Verwerkersovereenkomst</h3>
              <p className="text-[10px] text-muted-foreground mb-2">
                Download het addendum verwerkersovereenkomst AI-diensten (PDF) met uw praktijknaam ingevuld.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2"
                onClick={handleDownloadDpa}
              >
                <Download className="h-4 w-4" />
                Verwerkersovereenkomst downloaden
              </Button>
            </div>

            <Button onClick={handleSave} disabled={isSaving} className="w-full gap-2">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Opslaan
            </Button>
          </TabsContent>

          <TabsContent value="costs" className="mt-4">
            {practiceUserId && <UserCostsTab practiceUserId={practiceUserId} bron="eigen" />}
          </TabsContent>

          <TabsContent value="sip" className="mt-4 space-y-4">
            <p className="text-[10px] text-muted-foreground">
              Persoonlijke SIP-account. Leeg laten = praktijkstandaard gebruiken.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Extensie</Label>
                <Input value={sipExtension ?? ''} onChange={(e) => setSipExtension(e.target.value || null)} placeholder={(practiceDefaults.sip_extension as string) || '2001'} className="text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Wachtwoord</Label>
                <Input type="password" value={sipPassword ?? ''} onChange={(e) => setSipPassword(e.target.value || null)} className="text-xs" />
              </div>
            </div>
            <Button onClick={handleSave} disabled={isSaving} className="w-full gap-2">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Opslaan
            </Button>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
